// ===== Multiplayer config & helpers (client side) =====
// The PartyKit host comes ONLY from env (REACT_APP_MP_URL); never hardcoded.
// We normalize it to a bare hostname so PartySocket can pick ws/wss itself.

import PartySocket from 'partysocket';

// Dev-only test hook: localStorage "scopa.mp.devHost" points the dev server at a
// local `wrangler dev` (e.g. 127.0.0.1:8787). Ignored in production builds.
const DEV_HOST =
  process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
    ? window.localStorage.getItem('scopa.mp.devHost')
    : null;
const RAW = (DEV_HOST || process.env.REACT_APP_MP_URL || '').trim();
export const MP_HOST = RAW.replace(/^https?:\/\//, '').replace(/\/+$/, '');
// Configured only when a real host is present (placeholder token counts as not set).
export const MP_CONFIGURED = !!MP_HOST && !MP_HOST.includes('<');

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous I/O/0/1

export function makeRoomCode() {
  let s = '';
  for (let i = 0; i < 6; i++) {
    s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return s;
}

export function normalizeRoomCode(v) {
  return (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
}

// Persistent per-room seat token (enables reconnect to the same seat).
const tokenKey = (room) => `scopa.mp.${room}`;

export function loadToken(room) {
  try {
    return localStorage.getItem(tokenKey(room)) || null;
  } catch {
    return null;
  }
}

export function saveToken(room, token) {
  try {
    localStorage.setItem(tokenKey(room), token);
  } catch {
    /* storage unavailable — non fatal */
  }
}

// ===== Multiplayer identity (STEP 3A) =====
// Two separate, persistent, device-level values (kept independent from the
// per-room seat token above, which stays exactly as-is):
//   scopa.mp.playerId  -> technical id, generated once, owns a nickname
//   scopa.mp.nickname  -> human label (unique server-side via the registry)
// Designed so later steps can reuse this identity without re-doing it.
const PLAYER_ID_KEY = 'scopa.mp.playerId';
const NICKNAME_KEY = 'scopa.mp.nickname';

function randomId() {
  return (
    Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
  );
}

// Get the persistent device playerId, generating + storing it on first use.
export function ensurePlayerId() {
  try {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = `p_${randomId()}`;
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  } catch {
    return `p_${randomId()}`;
  }
}

export function loadNickname() {
  try {
    return localStorage.getItem(NICKNAME_KEY) || null;
  } catch {
    return null;
  }
}

export function saveNickname(nickname) {
  try {
    localStorage.setItem(NICKNAME_KEY, nickname);
  } catch {
    /* storage unavailable — non fatal */
  }
}

// True once the device has chosen a multiplayer nickname.
export function hasIdentity() {
  return !!loadNickname();
}

// Trim + collapse inner whitespace (mirror of the server normalization).
export function normalizeNickname(s) {
  return (s || '').trim().replace(/\s+/g, ' ');
}

// Reasonable validation, mirrored on the server (registry.js).
export function validateNickname(s) {
  const value = normalizeNickname(s);
  if (value.length < 3) return { ok: false, message: 'Il nickname deve avere almeno 3 caratteri.' };
  if (value.length > 16) return { ok: false, message: 'Il nickname può avere al massimo 16 caratteri.' };
  if (!/^[A-Za-z0-9 _-]+$/.test(value)) {
    return { ok: false, message: 'Usa solo lettere, numeri, spazio, trattino o underscore.' };
  }
  return { ok: true, value };
}

// One-shot connection to the central registry party to claim/affirm a nickname.
// Resolves { ok:true, nickname } or { ok:false, error } (error 'nickname_taken',
// 'invalid_nickname', 'timeout', 'connection', 'not_configured').
export function claimNickname(playerId, nickname) {
  return new Promise((resolve) => {
    if (!MP_HOST) return resolve({ ok: false, error: 'not_configured' });
    let done = false;
    let socket;
    const finish = (result) => {
      if (done) return;
      done = true;
      try {
        socket && socket.close();
      } catch {
        /* noop */
      }
      resolve(result);
    };
    try {
      socket = new PartySocket({ host: MP_HOST, party: 'registry', room: 'global' });
    } catch {
      return resolve({ ok: false, error: 'connection' });
    }
    const timer = setTimeout(() => finish({ ok: false, error: 'timeout' }), 8000);
    socket.addEventListener('open', () => {
      socket.send(JSON.stringify({ type: 'register', playerId, nickname }));
    });
    socket.addEventListener('message', (e) => {
      let m;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      if (m.type === 'registered') {
        clearTimeout(timer);
        finish({ ok: true, nickname: m.nickname });
      } else if (m.type === 'error') {
        clearTimeout(timer);
        finish({ ok: false, error: m.code });
      }
    });
    socket.addEventListener('error', () => {
      clearTimeout(timer);
      finish({ ok: false, error: 'connection' });
    });
  });
}
